/**
 * Environment parameters — fail-closed (zero-divergence doctrine): every
 * per-environment value is a Vite env parameter; a missing one throws at
 * startup instead of silently defaulting. NOTHING per-env lives in source.
 */
function requireEnv(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];
  if (!value) {
    throw new Error(`${name} build-time environment variable is required`);
  }
  return value;
}

/** An optional parameter: absent or blank means "not configured for this environment" (the feature stays hidden). */
function optionalEnv(name: keyof ImportMetaEnv): string | undefined {
  const value = import.meta.env[name];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

export const environment = {
  /** Detect API base, e.g. https://api.detect-staging.meshtek.io/api */
  apiUrl: requireEnv('VITE_API_URL'),
  /** Shared identity project (meshtek-auth-{env}) Firebase web config. */
  auth: {
    apiKey: requireEnv('VITE_AUTH_API_KEY'),
    authDomain: requireEnv('VITE_AUTH_DOMAIN'),
    projectId: requireEnv('VITE_AUTH_PROJECT_ID'),
    appId: requireEnv('VITE_AUTH_APP_ID'),
  },
  /** QA's feedback intake (`qaBugReport`), set in both apphosting files (#120). Optional locally: when absent the Help
   *  menu hides "Send feedback". */
  feedbackApiUrl: optionalEnv('VITE_FEEDBACK_API_URL'),
  /** The QA Tracker, linked for Meshtek staff in the Help menu. */
  qaTrackerUrl: optionalEnv('VITE_QA_TRACKER_URL'),
  /** Build revision, when the build exposes one; sent with feedback so a report names what it ran. */
  buildSha: optionalEnv('VITE_BUILD_SHA'),
} as const;
